import React, { useState } from 'react';
import { MessageSquare, Send, Paperclip, User, Clock, Search, Filter, ArrowLeft, SendHorizontal, PlusCircle, ChevronDown, ChevronRight, Check, X } from 'lucide-react';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import Drawer from '../../sharedComponents/Drawer';
import { Button } from '../components/adminModals';
import { communicationService, InternalMessage, PrimaFacieSubmission } from '../services/communicationService';
import { userService } from '../services/adminAuth';
import signalRService from '../../../services/signalRService';


const InternalCommunications: React.FC = () => {
    const [submissions, setSubmissions] = useState<PrimaFacieSubmission[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState<PrimaFacieSubmission | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // New Chat State
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [additionalDetails, setAdditionalDetails] = useState<string>('');
    const [chatMessages, setChatMessages] = useState<InternalMessage[]>([]);

    // Lookups
    const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
    const [allUsers, setAllUsers] = useState<{ id: string; name: string; roleId: string }[]>([]);

    const messageTemplates = [
        { id: 't1', label: 'Recommendation for new reviewer addition' },
        { id: 't2', label: 'Submission requires additional screening' },
        { id: 't3', label: 'Requesting feedback on current assessment' },
        { id: 't4', label: 'Proceeding with final approval' },
        { id: 't5', label: 'Rejecting due to technical inaccuracies' },
        { id: 't6', label: 'Assigning to a different thematic area' },
        { id: 't7', label: 'Other' }
    ];

    React.useEffect(() => {
        fetchSubmissions();
        fetchLookups();
        
        signalRService.startConnection();
        const unsubscribe = signalRService.onReceiveMessage((data: any) => {
            if (isDrawerOpen && selectedSubmission && (data.submissionId === selectedSubmission.id || data.SubmissionId === selectedSubmission.id)) {
                const newMsg: InternalMessage = {
                    id: data.id || Date.now().toString(),
                    message: data.message || data.Message,
                    senderName: data.senderName || data.SenderName || 'Internal System',
                    sendDateTime: data.sendDateTime || data.SendDateTime || new Date().toISOString(),
                    receiverName: data.receiverName || data.ReceiverName
                };
                setChatMessages(prev => [...prev, newMsg]);
            }
        });

        return () => {
            unsubscribe();
            signalRService.stopConnection();
        };
    }, [isDrawerOpen, selectedSubmission?.id]);

    const fetchSubmissions = async () => {
        setLoading(true);
        try {
            const res = await communicationService.getSubmissionsByPrimaFacie();
            if (res.isSuccess && res.data) {
                setSubmissions(res.data.data || []);
            }
        } catch (err) {
            } finally {
            setLoading(false);
        }
    };

    const fetchLookups = async () => {
        try {
            const rolesRes = await userService.getAdminRoles();
            if (rolesRes.isSuccess && rolesRes.data) {
                setRoles(rolesRes.data.map(r => ({ id: r.id.toString(), name: r.name })));
            }
            const usersRes = await userService.getAllUsers({ pageNumber: 1, pageSize: 100 });
            if (usersRes.isSuccess && usersRes.data) {
                setAllUsers(usersRes.data.data.map(u => ({ 
                    id: u.userId, 
                    name: u.person?.fullName || u.username,
                    roleId: u.roleId.toString()
                })));
            }
        } catch (err) {
            }
    };

    const handleOpenDiscussion = async (submission: PrimaFacieSubmission) => {
        setSelectedSubmission(submission);
        setIsDrawerOpen(true);
        setLoadingHistory(true);
        try {
            await signalRService.joinGroup(submission.id);
            const res = await communicationService.getMessageHistory(submission.id);
            if (res.isSuccess && res.data) {
                setChatMessages(res.data);
            }
        } catch (err) {
            } finally {
            setLoadingHistory(false);
        }
    };

    const handleCloseDiscussion = () => {
        if (selectedSubmission) {
            signalRService.leaveGroup(selectedSubmission.id);
        }
        setIsDrawerOpen(false);
        setChatMessages([]);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedTemplateId || selectedUsers.length === 0 || !selectedSubmission) return;

        const template = messageTemplates.find(t => t.id === selectedTemplateId)?.label;
        const fullContent = additionalDetails.trim() 
            ? `${template}: ${additionalDetails}`
            : template || '';

        try {
            const payload = {
                submissionId: selectedSubmission.id,
                receiverId: selectedUsers.join(','),
                message: fullContent
            };

            const res = await communicationService.sendMessage(payload);
            if (res.isSuccess) {
                setIsModalOpen(false);
                setSelectedTemplateId('');
                setAdditionalDetails('');
                setSelectedUsers([]);
                setSelectedRoleId('');
            }
        } catch (err) {
            }
    };

    const handleUserSelect = (userId: string) => {
        setSelectedUsers(prev => 
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const columns: TableColumn<PrimaFacieSubmission>[] = [
        {
            key: 'submissionNumber',
            label: 'S/N',
            render: (val) => <span className="font-mono text-xs font-semibold text-gray-500">{val}</span>
        },
        {
            key: 'title',
            label: 'Submission Title',
            render: (val, row) => (
                <div className="max-w-xs">
                    <div className="font-medium text-gray-900 truncate" title={val as string}>{val}</div>
                    <div className="text-xs text-gray-500">by {row.authors}</div>
                </div>
            )
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (val) => <span className="text-sm text-gray-600">{val || 'N/A'}</span>
        }
    ];

    const actions: TableAction<PrimaFacieSubmission>[] = [
        {
            icon: <MessageSquare size={18} />,
            title: "Discuss",
            onClick: (row) => handleOpenDiscussion(row),
            className: "text-[#088A61] hover:bg-[#088A61]/10 p-2 rounded-lg transition-colors"
        }
    ];

    return (
        <div className="w-full">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Internal Discussions</h1>
                <p className="text-gray-500 mt-1">Communicate and document reviewer recommendations with other administrators.</p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <ReusableTable
                    title="Submission Discussions"
                    subTitle="Track internal conversations for each manuscript"
                    columns={columns}
                    data={submissions}
                    actions={actions}
                    loading={loading}
                    showSearch={true}
                    searchPlaceholder="Search by ID, title or author..."
                />
            </div>

            <Drawer
                isOpen={isDrawerOpen}
                onClose={handleCloseDiscussion}
                title={selectedSubmission ? `Discussion: ${selectedSubmission.submissionNumber}` : 'Discussion'}
                width="max-w-xl"
            >
                <div className="flex flex-col h-[calc(100vh-80px)] bg-gray-50/50">
                    {/* Submission Context Mini-Header */}
                    {selectedSubmission && (
                        <div className="p-4 bg-white border-b border-gray-100 mb-4 rounded-lg shadow-sm mx-4 mt-2">
                            <h4 className="text-sm font-bold text-gray-900 line-clamp-1">{selectedSubmission.title}</h4>
                            <p className="text-xs text-gray-500 mt-1">Authors: {selectedSubmission.authors}</p>
                        </div>
                    )}

                    {/* Messages Thread */}
                    <div className="flex-1 overflow-y-auto px-4 space-y-6 pb-6">
                        {loadingHistory ? (
                            <div className="flex flex-col items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#088A61]"></div>
                                <p className="text-sm text-gray-500 mt-2">Loading history...</p>
                            </div>
                        ) : chatMessages.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center p-8">
                                <MessageSquare size={48} className="text-gray-200 mb-4" />
                                <p className="text-gray-500 font-medium">No discussion yet</p>
                            </div>
                        ) : (
                            chatMessages.map((msg) => (
                                <div 
                                    key={msg.id} 
                                    className="flex flex-col"
                                >
                                    <div className="flex items-center gap-2 mb-1 px-1">
                                        <span className="text-[11px] font-bold text-gray-900">{msg.senderName}</span>
                                        <span className="text-[10px] text-gray-400">
                                            {new Date(msg.sendDateTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                        </span>
                                    </div>
                                    <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm relative group hover:border-[#088A61]/30 transition-colors">
                                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                                        {msg.receiverName && (
                                            <div className="mt-2 pt-2 border-t border-gray-50 flex items-center gap-1.5">
                                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">To:</span>
                                                <span className="text-[10px] font-bold text-[#088A61] truncate">{msg.receiverName}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Chat Trigger Footer */}
                    <div className="p-4 bg-white border-t border-gray-100 flex justify-center">
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center gap-2 bg-[#088A61] text-white px-6 py-3 rounded-full font-bold shadow-lg shadow-[#088A61]/20 hover:scale-105 active:scale-95 transition-all"
                        >
                            <PlusCircle size={20} />
                            Start Chat
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Start Discussion Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg relative z-10 overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-[#088A61]/5">
                            <h3 className="text-lg font-bold text-gray-900">New Discussion</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                            {/* Role & User Selection Dropdowns */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select Role:</label>
                                    <select
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#088A61] transition-all"
                                        value={selectedRoleId}
                                        onChange={(e) => {
                                            setSelectedRoleId(e.target.value);
                                            setSelectedUsers([]); // Reset users when role changes
                                        }}
                                    >
                                        <option value="">Select a Role</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>{role.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedRoleId && (
                                    <div className="animate-in slide-in-from-left-2 duration-200 relative">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Select User(s):</label>
                                        <div 
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm cursor-pointer flex justify-between items-center group hover:border-[#088A61] transition-all"
                                            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                        >
                                            <span className="truncate text-gray-700">
                                                {selectedUsers.length === 0 
                                                    ? "Select Recipients" 
                                                    : `${selectedUsers.length} user(s) selected`}
                                            </span>
                                            <ChevronDown size={16} className={`text-gray-400 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                                        </div>

                                        {isUserDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-10" onClick={() => setIsUserDropdownOpen(false)}></div>
                                                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-xl z-20 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                                                    {allUsers.filter(u => u.roleId === selectedRoleId).map(user => (
                                                        <label 
                                                            key={user.id} 
                                                            className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                                                        >
                                                            <input 
                                                                type="checkbox" 
                                                                className="w-4 h-4 rounded border-gray-300 text-[#088A61] focus:ring-[#088A61]"
                                                                checked={selectedUsers.includes(user.id)}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    handleUserSelect(user.id);
                                                                }}
                                                            />
                                                            <span className={`text-sm ${selectedUsers.includes(user.id) ? 'font-bold text-[#088A61]' : 'text-gray-700'}`}>
                                                                {user.name}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Message Template Dropdown */}
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Message Template:</label>
                                <select
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#088A61] transition-all"
                                    value={selectedTemplateId}
                                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                                >
                                    <option value="">Select a template...</option>
                                    {messageTemplates.map(t => (
                                        <option key={t.id} value={t.id}>{t.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Additional Details */}
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Additional Details (Optional):</label>
                                <textarea
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#088A61] transition-all min-h-[100px] resize-none"
                                    placeholder="Enter specific details (e.g. reviewer names, links, etc.)"
                                    value={additionalDetails}
                                    onChange={(e) => setAdditionalDetails(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button 
                                onClick={() => setIsModalOpen(false)}
                                className="px-6 py-2.5 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleSendMessage}
                                disabled={!selectedTemplateId || selectedUsers.length === 0}
                                className={`px-8 py-2.5 rounded-xl text-sm font-bold shadow-lg transition-all flex items-center gap-2 ${
                                    selectedTemplateId && selectedUsers.length > 0
                                        ? 'bg-[#088A61] text-white hover:scale-105 active:scale-95'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                                }`}
                            >
                                <SendHorizontal size={18} />
                                Send Message
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InternalCommunications;
